'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Modal,
  Switch,
  Chip,
  cn,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useAuthenticatedApi } from '@/lib/useAuthenticatedApi';
import {
  detectPlatformSupport,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  getCurrentPushSubscription,
  testPushNotification,
  type PlatformSupport,
} from '@/lib/pushNotifications';
import {
  getVapidPublicKey,
  subscribeToPush,
  unsubscribeFromPush,
  checkPushSubscription,
} from '@/lib/notificationsApi';
import { toast } from 'sonner';

interface PushNotificationSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PushNotificationSettings({ isOpen, onClose }: PushNotificationSettingsProps) {
  const { authenticatedFetch } = useAuthenticatedApi();
  const [platformSupport, setPlatformSupport] = useState<PlatformSupport | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTestingNotification, setIsTestingNotification] = useState(false);

  // Check if user has an active push subscription
  const checkSubscriptionStatus = useCallback(async () => {
    try {
      const [localSubscription, backendSubscribed] = await Promise.all([
        getCurrentPushSubscription(),
        checkPushSubscription(authenticatedFetch),
      ]);

      // User is subscribed if they have both local and backend subscription
      setIsSubscribed(!!localSubscription && backendSubscribed);
    } catch (error) {
      console.error('[Push Settings] Failed to check subscription status:', error);
      setIsSubscribed(false);
    }
  }, [authenticatedFetch]);

  // Check platform support and subscription status on mount
  useEffect(() => {
    if (isOpen) {
      const support = detectPlatformSupport();
      setPlatformSupport(support);
      checkSubscriptionStatus();
    }
  }, [isOpen, checkSubscriptionStatus]);

  // Handle push notification toggle
  const handleTogglePushNotifications = useCallback(async (enabled: boolean) => {
    if (!platformSupport) return;

    setIsLoading(true);

    try {
      if (enabled) {
        // Subscribe to push notifications
        // 1. Get VAPID public key from backend
        const vapidPublicKey = await getVapidPublicKey(authenticatedFetch);

        // 2. Subscribe to push notifications (register service worker, request permission, get subscription)
        const subscription = await subscribeToPushNotifications(vapidPublicKey);

        if (!subscription) {
          throw new Error('Failed to create push subscription');
        }

        // 3. Send subscription to backend
        const deviceType = platformSupport.isIOS
          ? 'ios'
          : platformSupport.isAndroid
          ? 'android'
          : 'desktop';

        await subscribeToPush(
          {
            endpoint: subscription.endpoint,
            keys: subscription.keys,
            userAgent: navigator.userAgent,
            deviceType,
          },
          authenticatedFetch
        );

        setIsSubscribed(true);
        toast.success('Push notifications enabled!');
      } else {
        // Unsubscribe from push notifications
        // 1. Get current subscription
        const subscription = await getCurrentPushSubscription();

        if (subscription) {
          // 2. Unsubscribe from backend
          await unsubscribeFromPush(subscription.endpoint, authenticatedFetch);

          // 3. Unsubscribe locally
          await unsubscribeFromPushNotifications();
        }

        setIsSubscribed(false);
        toast.success('Push notifications disabled');
      }
    } catch (error) {
      console.error('[Push Settings] Failed to toggle push notifications:', error);
      
      if (error instanceof Error) {
        // Show the error message from subscribeToPushNotifications which includes specific guidance
        // The error message already contains actionable instructions for NotAllowedError, AbortError, etc.
        if (error.message.includes(':\\n')) {
          // Multi-line error message with instructions - show with longer duration
          toast.error(error.message, { duration: 8000 });
        } else if (error.message.includes('permission')) {
          toast.error('Notification permission denied. Please enable notifications in your browser settings.');
        } else {
          toast.error(`Failed to ${enabled ? 'enable' : 'disable'} push notifications: ${error.message}`, { duration: 5000 });
        }
      } else {
        toast.error(`Failed to ${enabled ? 'enable' : 'disable'} push notifications`);
      }

      // Revert state
      await checkSubscriptionStatus();
    } finally {
      setIsLoading(false);
    }
  }, [platformSupport, authenticatedFetch, checkSubscriptionStatus]);

  // Test push notification
  const handleTestNotification = useCallback(async () => {
    setIsTestingNotification(true);

    try {
      const success = await testPushNotification();

      if (success) {
        toast.success('Test notification sent!');
      } else {
        toast.error('Failed to send test notification');
      }
    } catch (error) {
      console.error('[Push Settings] Failed to test notification:', error);
      toast.error('Failed to send test notification');
    } finally {
      setIsTestingNotification(false);
    }
  }, []);

  if (!platformSupport) {
    return null;
  }

  return (
          <Modal.Backdrop isOpen={isOpen} onOpenChange={(v) => !v && onClose()}>
        <Modal.Container size="md">
          <Modal.Dialog>
        <Modal.Header className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Icon icon="solar:bell-bing-bold-duotone" width={24} className="text-accent" />
            <span>Push Notifications</span>
          </div>
        </Modal.Header>
        <Modal.Body>
          {/* Platform Support Info */}
          <div className="space-y-3">
            {/* Status Chips */}
            <div className="flex flex-wrap gap-2">
              <Chip
                size="sm"
                color={platformSupport.isPWA ? 'success' : 'default'}
                variant={platformSupport.isPWA ? 'soft' : 'secondary'}
              >
                <Icon
                  icon={platformSupport.isPWA ? 'solar:check-circle-bold' : 'solar:close-circle-bold'}
                  width={16}
                />
                <Chip.Label>{platformSupport.isPWA ? 'App Installed' : 'Not Installed'}</Chip.Label>
              </Chip>

              <Chip
                size="sm"
                color={platformSupport.supportsPush ? 'success' : 'warning'}
                variant={platformSupport.supportsPush ? 'soft' : 'secondary'}
              >
                <Icon
                  icon={platformSupport.supportsPush ? 'solar:check-circle-bold' : 'solar:info-circle-bold'}
                  width={16}
                />
                <Chip.Label>{platformSupport.supportsPush ? 'Push Supported' : 'Limited Support'}</Chip.Label>
              </Chip>

              {platformSupport.isIOS && (
                <Chip size="sm" variant="soft" color="default">
                  <Icon icon="solar:apple-bold" width={16} />
                  <Chip.Label>iOS Device</Chip.Label>
                </Chip>
              )}

              {platformSupport.isAndroid && (
                <Chip size="sm" variant="soft" color="default">
                  <Icon icon="solar:android-bold" width={16} />
                  <Chip.Label>Android Device</Chip.Label>
                </Chip>
              )}
              
              {/* Only show Brave chip on desktop (where special instructions apply) */}
              {platformSupport.isBrave && !platformSupport.isMobile && (
                <Chip size="sm" color="warning" variant="soft">
                  <Icon icon="solar:shield-warning-bold" width={16} />
                  <Chip.Label>Brave Browser</Chip.Label>
                </Chip>
              )}
            </div>

            {/* Install Prompt (if needed) */}
            {platformSupport.installPrompt && (
              <div className="p-3 rounded-lg bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800">
                <div className="flex items-start gap-2">
                  <Icon icon="solar:info-circle-bold" width={20} className="text-warning-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-warning-700 dark:text-warning-400 whitespace-pre-line">
                    {platformSupport.installPrompt}
                  </div>
                </div>
              </div>
            )}
            
            {/* Brave Browser Instructions */}
            {platformSupport.braveInstructions && (
              <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
                <div className="flex items-start gap-2">
                  <Icon icon="solar:shield-warning-bold" width={20} className="text-accent-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-accent-700 dark:text-accent-400 mb-1">
                      Brave Browser Detected
                    </p>
                    <div className="text-xs text-accent-700 dark:text-accent-400 whitespace-pre-line">
                      {platformSupport.braveInstructions}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Push Toggle */}
            {platformSupport.supportsPush && !platformSupport.needsHomeScreenInstall && (
              <button
                type="button"
                onClick={() => !isLoading && handleTogglePushNotifications(!isSubscribed)}
                disabled={isLoading}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer w-full text-left group",
                  isSubscribed
                    ? "bg-success-50/50 hover:bg-success-50 border border-success-200/60"
                    : "bg-muted-100 hover:bg-muted-200 border border-transparent",
                  isLoading && "opacity-50 cursor-not-allowed"
                )}
              >
                <div className="flex-1 flex items-start gap-2">
                  <Icon
                    icon={isSubscribed ? "solar:check-circle-bold" : "solar:bell-off-linear"}
                    className={cn(
                      "w-5 h-5 flex-shrink-0 mt-0.5 transition-colors",
                      isSubscribed ? "text-success-600" : "text-muted"
                    )}
                  />
                  <div>
                    <p className={cn(
                      "text-sm font-medium transition-colors",
                      isSubscribed ? "text-success-700" : "text-foreground"
                    )}>
                      {isSubscribed ? "Push Notifications Enabled" : "Enable Push Notifications"}
                    </p>
                    <p className="text-xs text-muted mt-1">
                      {isSubscribed
                        ? "You're receiving push notifications"
                        : "Receive notifications even when the app is closed"
                      }
                    </p>
                  </div>
                </div>
                <div className="pointer-events-none">
                  <Switch
                    isSelected={isSubscribed}
                    isReadOnly
                    size="sm"
                    aria-label="Push notifications status"
                  >
                    <Switch.Content>
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch.Content>
                  </Switch>
                </div>
              </button>
            )}

            {/* Test Notification Button */}
            {isSubscribed && (
              <Button
                size="sm"
                variant="secondary"
                
                onPress={handleTestNotification} {...({ isPending: isTestingNotification } as any)}
                fullWidth
              >{!isTestingNotification && <Icon icon="solar:test-tube-bold" width={18} />}
                Test Notification
              </Button>
            )}

            {/* Info Section */}
            <div className="pt-3 border-t border-border">
              <h4 className="text-xs font-semibold text-muted mb-2">About Push Notifications</h4>
              <ul className="text-xs text-muted space-y-1.5">
                <li className="flex items-start gap-2">
                  <Icon icon="solar:check-circle-bold" width={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Works on Android when installed from browser</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:check-circle-bold" width={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Works on iOS when added to home screen (iOS 16.4+)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:check-circle-bold" width={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span>Notifications work even when app is closed</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon icon="solar:shield-check-bold" width={16} className="text-accent mt-0.5 flex-shrink-0" />
                  <span>You can disable notifications anytime in browser settings</span>
                </li>
              </ul>
            </div>

            {/* Disabled State Message */}
            {!platformSupport.supportsPush && !platformSupport.needsHomeScreenInstall && (
              <div className="p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800">
                <div className="flex items-start gap-2">
                  <Icon icon="solar:close-circle-bold" width={20} className="text-danger-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-danger-700 dark:text-danger-400">
                    Push notifications are not supported on this device or browser. Please use a modern browser or install
                    the app from your browser for push notification support.
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="ghost" onPress={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
  );
}

